import { useCallback, useRef, useEffect } from 'react'

export function useEventSource() {
  const sse = useRef<EventSource | null>(null)
  const isConnecting = useRef(false)
  const currentUrl = useRef<string | null>(null)

  const subscribe = useCallback((url: string, callback: (data: any) => void) => {
    if (isConnecting.current && currentUrl.current === url) {
      return
    }

    if (sse.current && currentUrl.current !== url) {
      sse.current.close()
      sse.current = null
    }

    if (sse.current && currentUrl.current === url && sse.current.readyState !== EventSource.CLOSED) {
      return
    }

    try {
      isConnecting.current = true
      currentUrl.current = url

      sse.current = new EventSource(url, { withCredentials: true })

      sse.current.onopen = function () {
        isConnecting.current = false
      }

      sse.current.onmessage = function (event) {
        try {
          const data = JSON.parse(event?.data)
          if (data) {
            callback(data)
          }
        } catch (parseErr) {
          console.error('[SSE] Parse error:', parseErr)
        }
      }

      sse.current.onerror = function () {
        isConnecting.current = false
        
        if (sse.current?.readyState === EventSource.CLOSED) {
          currentUrl.current = null
        }
      }
    } catch (err) {
      console.error('[SSE] Subscribe error:', err)
      isConnecting.current = false
      currentUrl.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (sse.current) {
        sse.current.close()
        sse.current = null
        currentUrl.current = null
        isConnecting.current = false
      }
    }
  }, [])

  return { sse, subscribe }
}
